import React from 'react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import ContactBoxes from '../components/ContactBoxes';
import { COMPANY_INFO, SERVICES_LIST } from '../data/mockData';

export default function ErhvervIndustriPage() {
  return (
    <div className="space-y-16 pb-16">
      <SEO 
        title="Erhverv & Industri - K-TO Ventilationsteknik"
        description="Ventilationsløsninger, indregulering og serviceaftaler til erhverv, industri, produktionsvirksomheder og kontorer i hele Jylland og Fyn."
        slug="/erhverv-og-industri"
      />

      {/* Page Header */}
      <section className="bg-slate-900 text-white py-16 border-b border-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-4">
          <div className="inline-flex items-center gap-2 px-3.5 py-1 rounded-full bg-sky-500/20 text-sky-300 text-xs font-bold uppercase tracking-wider border border-sky-500/30">
            <i className="fa-solid fa-industry text-sky-400" aria-hidden="true"></i>
            <span>Målgruppe & B2B</span>
          </div>

          <h1 className="text-3xl sm:text-5xl font-black tracking-tight text-white">
            Erhverv og Industri
          </h1>

          <p className="text-slate-300 text-base sm:text-lg max-w-3xl leading-relaxed">
            Driftssikre ventilationsløsninger, løbende servicering og hurtig fejlfinding til virksomheder i hele Jylland og på Fyn.
          </p>
        </div>
      </section>

      {/* Main Content */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          
          <div className="lg:col-span-7 space-y-6">
            <h2 className="text-2xl sm:text-3xl font-extrabold text-slate-900 tracking-tight">
              Ventilationsservice tilpasset jeres bygninger og produktion
            </h2>

            <div className="prose prose-slate max-w-none text-slate-700 leading-relaxed space-y-4">
              <p className="text-lg font-semibold text-slate-900">
                Hos K-TO Ventilationsteknik forstår vi, at et driftsstop eller et dårligt indeklima i en erhvervsbygning koster både trivsel og produktivitet.
              </p>
              <p>
                Vi tilbyder professionel service, indregulering, rensning, fejlfinding og montering tilpasset de særlige krav i produktionslokaler, lagerhaller, kontormiljøer, institutioner og erhvervsejendomme.
              </p>
              <p>
                Med 30 års erfaring arbejder vi systematisk med forebyggende vedligeholdelse, så uforudsete nedbrud undgås, og anlæggets energiforbrug holdes på et fornuftigt niveau.
              </p>
            </div>

            <div className="pt-2">
              <a
                href={`tel:${COMPANY_INFO.phoneRaw}`}
                className="bg-sky-600 hover:bg-sky-700 text-white px-6 py-3.5 rounded-xl font-bold text-xs uppercase tracking-wider transition-colors inline-flex items-center gap-2 nowrap-contact"
              >
                <i className="fa-solid fa-phone" aria-hidden="true"></i>
                <span>Ring på 22 72 87 23</span>
              </a>
            </div>
          </div>

          <div className="lg:col-span-5">
            <div className="rounded-3xl overflow-hidden border border-slate-200 shadow-xl bg-white">
              <img 
                src="https://assets.auxodev.dk/assets/379e2bd1-51e7-4436-a4bf-bf31d3b287e1/ventilationservice-2-1024x683.jpg" 
                alt="Erhvervsventilation i teknikrum" 
                className="w-full h-80 object-cover"
              />
              <div className="p-6 bg-slate-900 text-white space-y-2">
                <span className="text-xs font-bold text-sky-400 uppercase tracking-wider block">
                  Forebyggende vedligeholdelse
                </span>
                <p className="text-xs text-slate-300">
                  Planlagte serviceaftaler sikrer overblik over filterudskiftning, sliddele og lovpligtigt eftersyn af brandautomatik.
                </p>
              </div>
            </div>
          </div>

        </div>

        {/* Industry Focus Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 pt-6">
          <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm space-y-3">
            <div className="w-12 h-12 rounded-xl bg-sky-50 text-sky-600 flex items-center justify-center text-xl font-bold">
              <i className="fa-solid fa-industry" aria-hidden="true"></i>
            </div>
            <h3 className="font-bold text-lg text-slate-900">Produktionsvirksomheder</h3>
            <p className="text-xs text-slate-600 leading-relaxed">
              Fjernelse af processtøv, kontrol af luftskifte og løbende servicering af tunge ventilationsaggregater.
            </p>
          </div>

          <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm space-y-3">
            <div className="w-12 h-12 rounded-xl bg-sky-50 text-sky-600 flex items-center justify-center text-xl font-bold">
              <i className="fa-solid fa-warehouse" aria-hidden="true"></i>
            </div>
            <h3 className="font-bold text-lg text-slate-900">Lager & Logistik</h3>
            <p className="text-xs text-slate-600 leading-relaxed">
              Stabile temperaturer, fugtstyring og balancering af store luftmængder i højlager og haller.
            </p>
          </div>

          <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm space-y-3">
            <div className="w-12 h-12 rounded-xl bg-sky-50 text-sky-600 flex items-center justify-center text-xl font-bold">
              <i className="fa-solid fa-building-user" aria-hidden="true"></i>
            </div>
            <h3 className="font-bold text-lg text-slate-900">Kontorer & Ejendomsdrift</h3>
            <p className="text-xs text-slate-600 leading-relaxed">
              Støjsvag drift, eliminering af trækgener og et sundt indeklima for medarbejdere og lejere.
            </p>
          </div>

          <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm space-y-3">
            <div className="w-12 h-12 rounded-xl bg-sky-50 text-sky-600 flex items-center justify-center text-xl font-bold">
              <i className="fa-solid fa-school" aria-hidden="true"></i>
            </div>
            <h3 className="font-bold text-lg text-slate-900">Institutioner & Skoler</h3>
            <p className="text-xs text-slate-600 leading-relaxed">
              Korrekt indregulering og kanalrensning for at forhindre skimmelsvamp og dårlig luftkvalitet.
            </p>
          </div>

          <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm space-y-3">
            <div className="w-12 h-12 rounded-xl bg-sky-50 text-sky-600 flex items-center justify-center text-xl font-bold">
              <i className="fa-solid fa-sliders" aria-hidden="true"></i>
            </div>
            <h3 className="font-bold text-lg text-slate-900">Indregulering & Målerapporter</h3>
            <p className="text-xs text-slate-600 leading-relaxed">
              Lovpligtig indregulering og tilsendelse af udførlig målerapport efter gennemført balancering.
            </p>
          </div>

          <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm space-y-3">
            <div className="w-12 h-12 rounded-xl bg-sky-50 text-sky-600 flex items-center justify-center text-xl font-bold">
              <i className="fa-solid fa-bolt" aria-hidden="true"></i>
            </div>
            <h3 className="font-bold text-lg text-slate-900">Akut Fejlfinding</h3>
            <p className="text-xs text-slate-600 leading-relaxed">
              Hurtig fejlfinding ved motorstop, defekte følere, sprængte remme eller styringssvigt.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Boxes */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <ContactBoxes />
      </section>
    </div>
  );
}
